// capacitor
import {
  InAppBrowser,
  DefaultSystemBrowserOptions,
  iOSViewStyle,
  AndroidViewStyle,
} from '@capacitor/inappbrowser';

// sentry
import * as Sentry from '@sentry/react';

// sessionStorage outlives the page reloads that signing in and out cause, so a
// popover opened before a reload is still known to be open after it
const POPOVER_OPEN_KEY = 'popoverOpen';

/**
 * getIsPopoverOpen reports whether a popover opened by __openInPopover may
 * still be open.
 */
export const getIsPopoverOpen = () => {
  try {
    return sessionStorage.getItem(POPOVER_OPEN_KEY) === 'true';
  } catch {
    // Unknown, so assume open: closing then retries rather than being skipped
    return true;
  }
};

export const setIsPopoverOpen = (isOpen: boolean) => {
  try {
    if (isOpen) {
      sessionStorage.setItem(POPOVER_OPEN_KEY, 'true');
    } else {
      sessionStorage.removeItem(POPOVER_OPEN_KEY);
    }
  } catch {
    // Without storage getIsPopoverOpen assumes open, which only costs retries
  }
};

InAppBrowser.addListener('browserClosed', () => {
  console.log('[globalMethods] IAB signaled browser closed');
  setIsPopoverOpen(false);
}).catch((err) => {
  console.error('[globalMethods] Failed to listen for IAB close:', err);
  Sentry.captureException(err, { tags: { 'iab.action': 'listen' } });
});

/**
 * __openInPopover is a function that opens a URL in the system browser.
 * It is used to intercept redirects and open them in a popover on iOS and Android.
 */
(window as any).__openInPopover = (url: string) => {
  console.log('[Intercepted Redirect]', url);
  setIsPopoverOpen(true);
  InAppBrowser.openInSystemBrowser({
    url: url.replace('"', ''),
    options: {
      ...DefaultSystemBrowserOptions,
      iOS: {
        ...DefaultSystemBrowserOptions.iOS,
        viewStyle: iOSViewStyle.FORM_SHEET,
      },
      android: {
        ...DefaultSystemBrowserOptions.android,
        viewStyle: AndroidViewStyle.BOTTOM_SHEET,
      },
    },
  }).catch((err) => {
    console.error('[Intercepted Redirect] Failed to open popover:', url, err);
    Sentry.captureException(err, { tags: { 'iab.action': 'open' } });
    setIsPopoverOpen(false);
  });
  return;
};
